import type { SRT_Row } from '@/shared/components/master-table'
import type { BadgeProps } from '@/shared/components/ui/badge'
import type { AnchorGeneratedFamilyModel } from '@/shared/generated'
import type { AnchorApprovalTableItem } from '../../lib/types'

import { useMemo } from 'react'
import { RiCheckboxCircleFill, RiCloseCircleFill } from '@remixicon/react'
import { ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ANCHOR_STATUS, getAnchorsTypesChoices } from '@/entities/anchor'
import { useOptimizationGroupQuery } from '@/entities/optimization-group'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import { Status, StatusLabel } from '@/shared/components/ui/status'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { useApprovalPageContext } from '../ApprovalPageProvider'
import AnchorFamilyApproveButton from './AnchorFamilyApproveButton'
import AnchorFamilyRejectButton from './AnchorFamilyRejectButton'
import ApprovalTableFamilyRulesDropdown from './ApprovalTableFamilyRulesDropdown'

type BadgeVariant = BadgeProps['variant']

export function ApprovalTableFamilyGroup({
	row,
}: {
	row: SRT_Row<AnchorApprovalTableItem>
}) {
	const { t } = useTranslation()

	const { selected, setSelected } = useApprovalPageContext()
	const generatedFamily = row.original as AnchorGeneratedFamilyModel
	const optimizationGroupId = generatedFamily?.optimization_group_id
	const generatedFamilyId = generatedFamily?.id
	const optimizationQuery = useOptimizationGroupQuery(optimizationGroupId, {
		enabled: !!optimizationGroupId,
	})
	const isSelected = useMemo(
		() => selected.includes(generatedFamilyId),
		[selected, generatedFamilyId]
	)
	const group = row.original as AnchorGeneratedFamilyModel
	const isFamilyInReview = group.status === ANCHOR_STATUS.REVIEW
	const generatedFamilyCompositeName = `${group.anchor_strategy_name} / ${group.name}`
	const anchorChoices = getAnchorsTypesChoices(t).find(
		(choice) => choice.value === group.anchor_type
	)
	const handleSelect = () => {
		setSelected((selected) =>
			isSelected
				? selected.filter((sku) => sku !== generatedFamilyId)
				: [...selected, generatedFamilyId]
		)
	}

	return (
		<div
			data-testid='render-group-container'
			className='flex w-full items-center justify-between rounded-sm py-2 pr-4'
		>
			<div className='flex w-full min-w-0 items-center gap-2 text-sm font-medium text-foreground'>
				<div className='flex h-16 items-start gap-2'>
					<Button
						variant='ghost'
						size='icon-sm'
						data-expanded={row.getIsExpanded()}
						onClick={row.getToggleExpandedHandler()}
						className='group data-[expanded=true]:rotate-90'
					>
						<ChevronRightIcon className='opacity-50 group-hover:opacity-100' />
					</Button>
					<Checkbox
						className='mt-1.5'
						disabled={!isFamilyInReview}
						checked={isSelected}
						onChange={handleSelect}
					/>
				</div>

				<div className='w-full min-w-0 space-y-2'>
					<p className='truncate font-bold'>
						<Tooltip>
							<TooltipTrigger asChild>
								<span className='text-muted-foreground uppercase'>{`${t('strategy')} : ${generatedFamilyCompositeName}`}</span>
							</TooltipTrigger>
							<TooltipContent>{generatedFamilyCompositeName}</TooltipContent>
						</Tooltip>
					</p>
					<div className='flex items-center justify-between'>
						<div className='flex items-center gap-2'>
							<Badge variant={anchorChoices?.variant as BadgeVariant}>
								{anchorChoices?.label || ''}
							</Badge>
							<Separator orientation='vertical' className='h-4!' />
							<div className='flex items-center gap-1 text-xs font-medium'>
								<span className='font-medium'>
									{group.anchor_generated_records.length}
								</span>
								<span className='text-muted-foreground'>
									{t('products_sku')}
								</span>
							</div>
							<Separator orientation='vertical' className='h-4!' />

							{optimizationQuery.isLoading ? (
								<div className='flex items-center justify-center'>
									<Spinner data-testid='og-spinner' />
								</div>
							) : (
								<span className='overflow-hidden text-xs font-medium text-ellipsis text-muted-foreground'>
									{optimizationQuery.data?.name || '-'}
								</span>
							)}
							<Separator orientation='vertical' className='h-4!' />
							{group.settings && (
								<ApprovalTableFamilyRulesDropdown settings={group.settings} />
							)}
						</div>
						<div className='flex gap-2'>
							{group.status === ANCHOR_STATUS.REVIEW && (
								<AnchorFamilyApproveButton id={generatedFamilyId} />
							)}
							{group.status === ANCHOR_STATUS.REVIEW && (
								<AnchorFamilyRejectButton id={generatedFamilyId} />
							)}
							{group.status === ANCHOR_STATUS.APPROVED && (
								<Status variant='success' data-testid='family-status-badge'>
									<RiCheckboxCircleFill
										data-icon='inline-start'
										className='size-4'
									/>
									<StatusLabel className='capitalize'>
										{t(group.status)}
									</StatusLabel>
								</Status>
							)}
							{group.status === ANCHOR_STATUS.REJECTED && (
								<Status variant='error' data-testid='family-status-badge'>
									<RiCloseCircleFill
										data-icon='inline-start'
										className='size-4'
									/>
									<StatusLabel className='capitalize'>
										{t(group.status)}
									</StatusLabel>
								</Status>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
